import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Tables } from '../lib/database.types';

const QUERY_KEY = 'blog_posts';

export const useBlogPosts = (options = {}) => {
  const { 
    isPublished = true, 
    limit = 10, 
    offset = 0,
    language = 'fr',
    tagSlug = null
  } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { isPublished, limit, offset, language, tagSlug }],
    queryFn: async () => {
      let query = supabase
        .from(Tables.BLOG_POSTS)
        .select(`
          *,
          translations:blog_posts_translations(title, content, excerpt, language_code),
          tags:blog_post_tags(
            tag:tags(id, name, slug)
          )
        `)
        .order('display_order', { ascending: true })
        .range(offset, offset + limit - 1);

      if (isPublished !== null) {
        query = query.eq('is_published', isPublished);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      let posts = data.map(post => ({
        ...post,
        title: post.translations?.find(t => t.language_code === language)?.title || post.translations?.[0]?.title,
        content: post.translations?.find(t => t.language_code === language)?.content || post.translations?.[0]?.content,
        excerpt: post.translations?.find(t => t.language_code === language)?.excerpt || post.translations?.[0]?.excerpt,
      }));

      if (tagSlug) {
        posts = posts.filter(post => 
          post.tags?.some(t => t.tag?.slug === tagSlug)
        );
      }

      return posts;
    },
  });
};

export const useBlogPost = (slug) => {
  return useQuery({
    queryKey: [QUERY_KEY, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(Tables.BLOG_POSTS)
        .select(`
          *,
          translations:blog_posts_translations(title, content, excerpt, language_code),
          tags:blog_post_tags(
            tag:tags(id, name, slug)
          )
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
};

export const useCreateBlogPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (post) => {
      const { data, error } = await supabase
        .from(Tables.BLOG_POSTS)
        .insert(post)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateBlogPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from(Tables.BLOG_POSTS)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteBlogPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from(Tables.BLOG_POSTS)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useTags = () => {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(Tables.TAGS)
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    },
  });
};

export default useBlogPosts;
